import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { CalendarPage, MonthData } from '@traquility/ui/diary/CalendarPage';
import type { DiaryDay, FilterOption } from '@traquility/ui/diary/AgendaPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

interface CalendarPageContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  iconBaseUrl?: string;
  initialData?: MonthData;
  initialMonth?: string;
  fieldIds?: string[];
}

const CALENDAR_QUERY = `
  query CalendarDays($month: String, $fieldIds: [ID!]) {
    calendarDays(month: $month, fieldIds: $fieldIds) {
      diaryDays {
        id
        date
        mood
        energy
        note
        activities {
          id
          active
          value
          field {
            id
            name
            icon
            score
          }
        }
      }
      month
      dateFrom
      dateTill
    }
  }
`;

const CATEGORIES_QUERY = `
  query DiaryCategories {
    diaryCategories {
      id
      name
      fields {
        id
        name
        icon
        score
      }
      fieldSets {
        id
        name
        fields {
          id
          name
          icon
          score
        }
      }
    }
  }
`;

async function fetchCalendarDays({
  endpoint,
  csrfToken,
  month,
  fieldIds,
}: {
  endpoint: string;
  csrfToken: string;
  month?: string;
  fieldIds?: string[];
}): Promise<MonthData> {
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      query: CALENDAR_QUERY,
      variables: { month, fieldIds },
    }),
  });

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || 'GraphQL error');
  }

  return result.data.calendarDays;
}

async function fetchCategories({
  endpoint,
  csrfToken,
}: {
  endpoint: string;
  csrfToken: string;
}): Promise<FilterOption[]> {
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      query: CATEGORIES_QUERY,
    }),
  });

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || 'GraphQL error');
  }

  // Flatten all fields from categories and field sets with widgetName and fieldSetName
  const fields: FilterOption[] = [];
  for (const category of result.data.diaryCategories) {
    // Add direct fields
    for (const field of category.fields) {
      if (field.icon) {
        fields.push({
          id: field.id,
          name: field.name,
          icon: field.icon,
          score: field.score,
          widgetName: category.name,
          fieldSetName: null,
        });
      }
    }
    // Add fields from field sets
    for (const fieldSet of category.fieldSets) {
      for (const field of fieldSet.fields) {
        if (field.icon) {
          fields.push({
            id: field.id,
            name: field.name,
            icon: field.icon,
            score: field.score,
            widgetName: category.name,
            fieldSetName: fieldSet.name,
          });
        }
      }
    }
  }

  return fields;
}

// Helper to get the previous month string
function getPreviousMonth(month: string): string {
  const [year, monthNum] = month.split('-').map(Number);
  let prevMonth = monthNum - 1;
  let prevYear = year;

  if (prevMonth < 1) {
    prevMonth = 12;
    prevYear = year - 1;
  }

  return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
}

function CalendarPageInner({
  graphqlEndpoint,
  csrfToken,
  iconBaseUrl = '/assets',
  initialData,
  initialMonth,
  fieldIds: initialFieldIds = [],
}: CalendarPageContainerProps) {
  const currentMonthStr = new Date().toISOString().substring(0, 7);
  const startMonth = initialMonth || currentMonthStr;
  const isLoadingRef = useRef(false); // Synchronous flag to prevent race conditions

  // State for infinite scroll
  const [months, setMonths] = useState<MonthData[]>(initialData ? [initialData] : []);
  const [oldestMonth, setOldestMonth] = useState<string>(startMonth);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [filteredFieldIds, setFilteredFieldIds] = useState<string[]>(initialFieldIds);
  const [availableFields, setAvailableFields] = useState<FilterOption[]>([]);
  const [usedInitialData, setUsedInitialData] = useState(!!initialData && initialFieldIds.length === 0);

  // Fetch available fields for filtering
  useEffect(() => {
    fetchCategories({ endpoint: graphqlEndpoint, csrfToken })
      .then(setAvailableFields)
      .catch(err => console.error('Failed to fetch categories:', err));
  }, [graphqlEndpoint, csrfToken]);

  // Initial data fetch (first month) - skip if we have initialData
  const { isLoading: isInitialLoading } = useQuery({
    queryKey: ['calendarDays', startMonth, filteredFieldIds],
    queryFn: async () => {
      const data = await fetchCalendarDays({
        endpoint: graphqlEndpoint,
        csrfToken,
        month: startMonth,
        fieldIds: filteredFieldIds.length > 0 ? filteredFieldIds : undefined,
      });
      setMonths([data]);
      setOldestMonth(data.month);
      return data;
    },
    enabled: !usedInitialData,
  });

  // Load more months (older months)
  const handleLoadMore = useCallback(async () => {
    // Use ref for synchronous check to prevent race conditions from IntersectionObserver
    if (isLoadingRef.current || !hasMore) return;

    isLoadingRef.current = true;
    setIsLoadingMore(true);
    try {
      const prevMonth = getPreviousMonth(oldestMonth);
      const data = await fetchCalendarDays({
        endpoint: graphqlEndpoint,
        csrfToken,
        month: prevMonth,
        fieldIds: filteredFieldIds.length > 0 ? filteredFieldIds : undefined,
      });

      setMonths(prev => [...prev, data]);
      setOldestMonth(data.month);

      // Stop loading more if we've gone back far enough (e.g., 2 years)
      const twoYearsAgo = new Date();
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      const twoYearsAgoMonth = twoYearsAgo.toISOString().substring(0, 7);
      if (data.month <= twoYearsAgoMonth) {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load more months:', err);
    } finally {
      isLoadingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [graphqlEndpoint, csrfToken, oldestMonth, hasMore, filteredFieldIds]);

  // Handle filter change - reset and reload
  const handleFilterChange = useCallback((newFieldIds: string[]) => {
    setFilteredFieldIds(newFieldIds);
    setMonths([]);
    setOldestMonth(startMonth);
    setHasMore(true);
    setUsedInitialData(false); // Force refetch
    isLoadingRef.current = false; // Reset loading flag when filters change
  }, [startMonth]);

  const handleDayClick = useCallback((date: string) => {
    window.location.href = `/diary/days/${date}`;
  }, []);

  // Show loading only on initial load
  if (isInitialLoading && months.length === 0) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <CalendarPage
      months={months}
      onDayClick={handleDayClick}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      isLoading={isLoadingMore}
      iconBaseUrl={iconBaseUrl}
      availableFields={availableFields}
      filteredFieldIds={filteredFieldIds}
      onFilterChange={handleFilterChange}
    />
  );
}

export function CalendarPageContainer(props: CalendarPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <CalendarPageInner {...props} />
    </QueryClientProvider>
  );
}

export function mountCalendarPage(
  container: HTMLElement,
  props: CalendarPageContainerProps
): Root {
  const root = createRoot(container);
  root.render(<CalendarPageContainer {...props} />);
  return root;
}
